/**
 * Central theme configuration file.
 *
 * To add a new theme:
 *   1. Add its name to ThemeName
 *   2. Add its token values to the `themes` record
 *   3. Done — ThemeContext, components, and Tailwind need no changes
 *
 * All Tailwind color classes (bg-surface, text-text-primary, etc.) reference
 * these CSS custom properties via tailwind.config.js.
 */

export type ThemeName = 'light' | 'dark';
// Future: | 'midnight' | 'solarized' | 'high-contrast'

export interface ThemeTokens {
  // ── Backgrounds ────────────────────────────────────────────────────────────
  '--color-bg': string; // page background
  '--color-surface': string; // card / panel background
  '--color-surface-2': string; // nested panel / input / code background

  // ── Borders ────────────────────────────────────────────────────────────────
  '--color-border': string;
  '--color-border-strong': string;

  // ── Text ───────────────────────────────────────────────────────────────────
  '--color-text-primary': string;
  '--color-text-secondary': string;
  '--color-text-muted': string;

  // ── Brand / interactive ────────────────────────────────────────────────────
  '--color-brand': string;
  '--color-brand-hover': string;
  '--color-brand-text': string; // text on brand-colored backgrounds

  // ── Status ─────────────────────────────────────────────────────────────────
  '--color-success': string;
  '--color-warning': string;
  '--color-error': string;
  '--color-info': string;

  // ── Severity (SeverityBadge, CommentItem) ─────────────────────────────────
  '--color-severity-critical': string;
  '--color-severity-critical-bg': string;
  '--color-severity-warning': string;
  '--color-severity-warning-bg': string;
  '--color-severity-suggestion': string;
  '--color-severity-suggestion-bg': string;
  '--color-severity-nitpick': string;
  '--color-severity-nitpick-bg': string;

  // ── Risk level (RiskBadge) ─────────────────────────────────────────────────
  '--color-risk-high': string;
  '--color-risk-high-bg': string;
  '--color-risk-medium': string;
  '--color-risk-medium-bg': string;
  '--color-risk-low': string;
  '--color-risk-low-bg': string;
}

export const themes: Record<ThemeName, ThemeTokens> = {
  light: {
    '--color-bg': '#f8fbff',
    '--color-surface': '#ffffff',
    '--color-surface-2': '#eef6ff',
    '--color-border': '#dbeafe',
    '--color-border-strong': '#93c5fd',
    '--color-text-primary': '#0f172a',
    '--color-text-secondary': '#486281',
    '--color-text-muted': '#6b7f99',
    '--color-brand': '#3b82f6',
    '--color-brand-hover': '#2563eb',
    '--color-brand-text': '#ffffff',
    '--color-success': '#16a34a',
    '--color-warning': '#d97706',
    '--color-error': '#dc2626',
    '--color-info': '#0284c7',
    '--color-severity-critical': '#dc2626',
    '--color-severity-critical-bg': '#fef2f2',
    '--color-severity-warning': '#d97706',
    '--color-severity-warning-bg': '#fff7ed',
    '--color-severity-suggestion': '#2563eb',
    '--color-severity-suggestion-bg': '#eff6ff',
    '--color-severity-nitpick': '#64748b',
    '--color-severity-nitpick-bg': '#f1f5f9',
    '--color-risk-high': '#dc2626',
    '--color-risk-high-bg': '#fef2f2',
    '--color-risk-medium': '#d97706',
    '--color-risk-medium-bg': '#fff7ed',
    '--color-risk-low': '#16a34a',
    '--color-risk-low-bg': '#f0fdf4',
  },

  dark: {
    '--color-bg': '#020617',
    '--color-surface': '#040b1f',
    '--color-surface-2': '#0b1633',
    '--color-border': '#132548',
    '--color-border-strong': '#1d4b8f',
    '--color-text-primary': '#e5eeff',
    '--color-text-secondary': '#a9bddf',
    '--color-text-muted': '#7f96bb',
    '--color-brand': '#38bdf8',
    '--color-brand-hover': '#0ea5e9',
    '--color-brand-text': '#020617',
    '--color-success': '#22c55e',
    '--color-warning': '#f59e0b',
    '--color-error': '#fb7185',
    '--color-info': '#38bdf8',
    '--color-severity-critical': '#fb7185',
    '--color-severity-critical-bg': '#3b0b16',
    '--color-severity-warning': '#fbbf24',
    '--color-severity-warning-bg': '#422006',
    '--color-severity-suggestion': '#7dd3fc',
    '--color-severity-suggestion-bg': '#082f49',
    '--color-severity-nitpick': '#a4b6d4',
    '--color-severity-nitpick-bg': '#111f3e',
    '--color-risk-high': '#fb7185',
    '--color-risk-high-bg': '#3b0b16',
    '--color-risk-medium': '#fbbf24',
    '--color-risk-medium-bg': '#422006',
    '--color-risk-low': '#4ade80',
    '--color-risk-low-bg': '#052e16',
  },
};

export const DEFAULT_THEME: ThemeName = 'light';
